// ============================================================
// Toolbar — ChartFlow
// ============================================================

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { useSongStore } from '../../store/songStore';
import { formatKey } from '../../core/formatter';
import { ThemeSwitcher, useTheme } from './ThemeSwitcher';
import { SyncIndicator } from './SyncIndicator';
import { useLibraryStore } from '../../store/libraryStore';
import { useAuthStore } from '../../store/authStore';
import { InvitesPanel } from '../auth/InvitesPanel';
import { PresenceAvatars } from './PresenceLayer';
import { KeyDetectButton } from './KeyDetectButton';
import { extractChords } from '../../core/keyDetection';
import { MAJOR_KEYS, MINOR_KEYS } from '../../core/transposer';
import { PlaybackControls, type SynthMode } from './PlaybackControls';
import { usePlayback } from './usePlayback';
import type { DisplayMode, Key, TimeSignature, AnalysisSettings } from '../../core/types';

const TIME_SIGNATURES: TimeSignature[] = [
  { numerator: 4, denominator: 4 },
  { numerator: 3, denominator: 4 },
  { numerator: 2, denominator: 4 },
  { numerator: 6, denominator: 8 },
  { numerator: 9, denominator: 8 },
  { numerator: 5, denominator: 4 },
  { numerator: 7, denominator: 8 },
];

const DISPLAY_MODES: { value: DisplayMode; label: string }[] = [
  { value: 'nashville', label: 'NNS' },
  { value: 'roman',     label: 'ROM' },
  { value: 'chords',    label: 'CHD' },
];

// ------------------------------------------------------------
// PORTAL DROPDOWN HOOK
// ------------------------------------------------------------

interface PortalDropdownProps {
  open: boolean;
  triggerRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  align?: 'left' | 'right';
  children: React.ReactNode;
}

function PortalDropdown({ open, triggerRef, onClose, align = 'left', children }: PortalDropdownProps) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top:  rect.bottom + window.scrollY + 6,
      left: align === 'left'
        ? rect.left + window.scrollX
        : rect.right + window.scrollX,
    });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const portal = document.getElementById('toolbar-portal-dropdown');
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (portal?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, onClose]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      id="toolbar-portal-dropdown"
      className="dropdown"
      style={{
        position:  'absolute',
        top:       pos.top,
        ...(align === 'left'
          ? { left: pos.left }
          : { right: window.innerWidth - pos.left }),
        zIndex: 9999,
        minWidth: 120,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

// ------------------------------------------------------------
// KEY SELECTOR
// ------------------------------------------------------------

function KeySelector({ currentKey, onSelect }: { currentKey: Key; onSelect: (key: Key) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <div className="relative">
      <button ref={ref} className="metadata-badge" onClick={() => setOpen((v) => !v)} title="Change key">
        <span className="text-ink-muted text-[10px]">KEY</span>
        <span className="text-ink-primary font-semibold">{formatKey(currentKey)}</span>
      </button>
      <PortalDropdown open={open} triggerRef={ref} onClose={() => setOpen(false)} align="left">
        <div className="p-3 w-72">
          <div className="text-[9px] font-label tracking-widest uppercase text-ink-muted mb-2">Major</div>
          <div className="grid grid-cols-6 gap-1 mb-3">
            {MAJOR_KEYS.map((k) => (
              <button key={k.root + k.scale} onClick={() => { onSelect(k); setOpen(false); }}
                className={['text-xs py-1 rounded border transition-colors font-mono',
                  currentKey.root === k.root && currentKey.scale === k.scale
                    ? 'border-accent text-white bg-accent'
                    : 'border-rule text-ink-secondary hover:border-ink-muted hover:bg-hover'].join(' ')}>
                {k.root}
              </button>
            ))}
          </div>
          <div className="text-[9px] font-label tracking-widest uppercase text-ink-muted mb-2">Minor</div>
          <div className="grid grid-cols-6 gap-1">
            {MINOR_KEYS.map((k) => (
              <button key={k.root + k.scale} onClick={() => { onSelect(k); setOpen(false); }}
                className={['text-xs py-1 rounded border transition-colors font-mono',
                  currentKey.root === k.root && currentKey.scale === k.scale
                    ? 'border-accent text-white bg-accent'
                    : 'border-rule text-ink-secondary hover:border-ink-muted hover:bg-hover'].join(' ')}>
                {k.root}m
              </button>
            ))}
          </div>
        </div>
      </PortalDropdown>
    </div>
  );
}

// ------------------------------------------------------------
// TIME SIGNATURE SELECTOR
// ------------------------------------------------------------

function TimeSigSelector({ current, onChange }: { current: TimeSignature; onChange: (ts: TimeSignature) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <div className="relative">
      <button ref={ref} className="metadata-badge" onClick={() => setOpen((v) => !v)} title="Change time signature">
        <span className="text-ink-muted text-[10px]">TIME</span>
        <span className="text-ink-primary font-semibold font-mono">{current.numerator}/{current.denominator}</span>
      </button>
      <PortalDropdown open={open} triggerRef={ref} onClose={() => setOpen(false)} align="left">
        <div className="p-2 w-36">
          {TIME_SIGNATURES.map((ts) => {
            const isActive = ts.numerator === current.numerator && ts.denominator === current.denominator;
            return (
              <button key={`${ts.numerator}/${ts.denominator}`}
                onClick={() => { onChange(ts); setOpen(false); }}
                className={['w-full text-left px-3 py-1.5 text-sm font-mono rounded transition-colors',
                  isActive ? 'bg-accent text-white' : 'text-ink-secondary hover:bg-hover'].join(' ')}>
                {ts.numerator}/{ts.denominator}
              </button>
            );
          })}
        </div>
      </PortalDropdown>
    </div>
  );
}

// ------------------------------------------------------------
// THEME DROPDOWN
// ------------------------------------------------------------

function ThemeDropdown({ themeId, setThemeId }: { themeId: string; setThemeId: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <div className="relative">
      <button ref={ref} className="metadata-badge" onClick={() => setOpen((v) => !v)} title="Change theme">
        <span className="text-ink-muted text-[10px]">THEME</span>
        <span className="text-ink-primary">◑</span>
      </button>
      <PortalDropdown open={open} triggerRef={ref} onClose={() => setOpen(false)} align="right">
        <div className="p-3 w-48">
          <div className="text-[9px] font-label tracking-widest uppercase text-ink-muted mb-2">Appearance</div>
          <ThemeSwitcher themeId={themeId} setThemeId={(id) => { setThemeId(id); setOpen(false); }} />
        </div>
      </PortalDropdown>
    </div>
  );
}

// ------------------------------------------------------------
// FILE MENU
// ------------------------------------------------------------

function FileMenu({ isDirty, onNew, onSave, onOpen, onExportJSON, onPrint }: {
  isDirty: boolean; onNew: () => void; onSave: () => void;
  onOpen: () => void; onExportJSON: () => void; onPrint: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const item = (label: string, action: () => void, shortcut?: string) => (
    <button onClick={() => { action(); setOpen(false); }}
      className="w-full flex items-center justify-between px-3 py-2 text-sm text-ink-secondary hover:bg-hover rounded transition-colors font-mono">
      <span>{label}</span>
      {shortcut && <span className="text-[10px] text-ink-muted ml-4">{shortcut}</span>}
    </button>
  );
  return (
    <div className="relative">
      <button ref={ref}
        className={['metadata-badge relative', isDirty ? 'border-accent/50' : ''].join(' ')}
        onClick={() => setOpen((v) => !v)}>
        <span className="text-ink-primary">File</span>
        {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block ml-1" title="Unsaved changes" />}
      </button>
      <PortalDropdown open={open} triggerRef={ref} onClose={() => setOpen(false)} align="right">
        <div className="p-1.5 w-52">
          {item('New chart',    onNew,        '⌘N')}
          {item('Open…',       onOpen,        '⌘O')}
          <div className="border-t border-rule my-1" />
          {item('Save',        onSave,        '⌘S')}
          {item('Export JSON', onExportJSON)}
          <div className="border-t border-rule my-1" />
          {item('Print / PDF', onPrint,       '⌘P')}
        </div>
      </PortalDropdown>
    </div>
  );
}

// ------------------------------------------------------------
// ANALYSIS PANEL
// ------------------------------------------------------------

function AnalysisPanel({ settings: rawSettings, onChange }: {
  settings: AnalysisSettings;
  onChange: (partial: Partial<AnalysisSettings>) => void;
}) {
  const settings: AnalysisSettings = rawSettings ?? {
    enabled: false, showFunction: true, showBorrowed: true,
    showDegree: false, showSecondary: true,
  };
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const layers: Array<{ key: keyof AnalysisSettings; label: string; description: string }> = [
    { key: 'showFunction',  label: 'Function',  description: 'T · S · D · BD' },
    { key: 'showBorrowed',  label: 'Borrowed',  description: 'Marks borrowed chords' },
    { key: 'showDegree',    label: 'Degree',    description: 'Roman numeral below chord' },
    { key: 'showSecondary', label: 'Secondary', description: 'V/V · V/IV labels' },
  ];
  return (
    <div className="relative">
      <button
        ref={ref}
        onClick={() => { if (!settings.enabled) { onChange({ enabled: true }); } else { setOpen((v) => !v); } }}
        onContextMenu={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        className={['text-[11px] font-mono px-2 py-1 rounded border transition-colors',
          settings.enabled
            ? 'bg-accent text-white border-accent'
            : 'border-rule text-ink-muted hover:bg-hover hover:text-ink-secondary'].join(' ')}
        title={settings.enabled ? 'Analysis on — click to configure' : 'Enable harmonic analysis'}
      >
        ANA
      </button>
      {settings.enabled && (
        <button onClick={() => onChange({ enabled: false })}
          className="ml-0.5 text-[9px] text-accent/70 hover:text-accent font-mono"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
          title="Turn off analysis">✕</button>
      )}
      {settings.enabled && (
        <PortalDropdown open={open} triggerRef={ref} onClose={() => setOpen(false)} align="left">
          <div className="p-3 w-52">
            <div className="text-[9px] font-label tracking-widest uppercase text-ink-muted mb-3">Analysis layers</div>
            {layers.map((layer) => {
              const isOn = settings[layer.key] as boolean;
              return (
                <div key={layer.key} className="flex items-center justify-between mb-2 last:mb-0">
                  <div>
                    <div className="text-xs font-mono text-ink-primary">{layer.label}</div>
                    <div className="text-[10px] font-mono text-ink-muted">{layer.description}</div>
                  </div>
                  <button onClick={() => onChange({ [layer.key]: !isOn })}
                    className={['text-[10px] font-mono px-2 py-0.5 rounded border transition-colors ml-3 flex-shrink-0',
                      isOn ? 'bg-accent text-white border-accent' : 'border-rule text-ink-muted hover:bg-hover'].join(' ')}>
                    {isOn ? 'On' : 'Off'}
                  </button>
                </div>
              );
            })}
          </div>
        </PortalDropdown>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// MAIN COMPONENT: Toolbar
// ------------------------------------------------------------

interface ToolbarProps {
  onShowAuth:             () => void;
  onShowShare:            () => void;
  onShowPrint:            () => void;
  onSongLoad:             (song: import('../../core/types').Song) => void;
  annotationMode:         boolean;
  onToggleAnnotationMode: () => void;
  docsOpen:               boolean;
  onToggleDocs:           () => void;
}

export function Toolbar({
  onShowAuth,
  onShowShare,
  onShowPrint,
  onSongLoad,
  annotationMode,
  onToggleAnnotationMode,
  docsOpen,
  onToggleDocs,
}: ToolbarProps) {
  const song                 = useSongStore((s) => s.song);
  const isDirty              = useSongStore((s) => s.isDirty);
  const setSongTitle         = useSongStore((s) => s.setSongTitle);
  const setSongKey           = useSongStore((s) => s.setSongKey);
  const setSongTimeSignature = useSongStore((s) => s.setSongTimeSignature);
  const setDisplayMode       = useSongStore((s) => s.setDisplayMode);
  const setAnalysisSettings  = useSongStore((s) => s.setAnalysisSettings);
  const setSongBarsPerRow    = useSongStore((s) => s.setSongBarsPerRow);
  const setSongTempo         = useSongStore((s) => s.setSongTempo);
  const setSongFeel          = useSongStore((s) => s.setSongFeel);
  const setSongNote          = useSongStore((s) => s.setSongNote);
  const transposeBySemitones = useSongStore((s) => s.transposeBySemitones);
  const saveToLocalStorage   = useSongStore((s) => s.saveToLocalStorage);
  const loadFromLocalStorage = useSongStore((s) => s.loadFromLocalStorage);
  const exportToJSON         = useSongStore((s) => s.exportToJSON);
  const importFromJSON       = useSongStore((s) => s.importFromJSON);
  const newSong              = useSongStore((s) => s.newSong);
  const undo                 = useSongStore((s) => s.undo);
  const redo                 = useSongStore((s) => s.redo);
  const canUndo              = useSongStore((s) => s.canUndo);
  const canRedo              = useSongStore((s) => s.canRedo);

  const { themeId, setThemeId } = useTheme();
  const openLibrary = useLibraryStore((s) => s.openLibrary);
  const user        = useAuthStore((s) => s.user);
  const signOut     = useAuthStore((s) => s.signOut);

  // FIX: destructure the two new volume pairs from usePlayback
  const {
    playing, cursor, toggle, stop, isLoading,
    showClick, toggleClick,
    chordVolume, setChordVolume,
    clickVolume, setClickVolume,
  } = usePlayback({ song });

  const [synthMode, setSynthMode] = useState<SynthMode>('guitar');
  const handleSynthMode = useCallback((mode: SynthMode) => { setSynthMode(mode); }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== ' ') return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggle]);

  const [titleValue, setTitleValue]     = useState(song.title);
  const [editingTitle, setEditingTitle] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setTitleValue(song.title); }, [song.title]);
  useEffect(() => { if (editingTitle) titleRef.current?.select(); }, [editingTitle]);
  const commitTitle = useCallback(() => {
    const t = titleValue.trim() || 'Untitled';
    setSongTitle(t);
    setEditingTitle(false);
  }, [titleValue, setSongTitle]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = useCallback(() => {
    saveToLocalStorage();
    const json = exportToJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${song.title.replace(/\s+/g, '-').toLowerCase()}.hchart`; a.click();
    URL.revokeObjectURL(url);
  }, [saveToLocalStorage, exportToJSON, song.title]);

  const handleOpen = useCallback(() => { fileInputRef.current?.click(); }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const json = ev.target?.result as string;
      const result = importFromJSON(json);
      if (!result.ok) alert(`Could not open file: ${result.error}`);
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [importFromJSON]);

  const handleExportJSON = useCallback(() => {
    const json = exportToJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${song.title.replace(/\s+/g, '-').toLowerCase()}.hchart`; a.click();
    URL.revokeObjectURL(url);
  }, [exportToJSON, song.title]);

  const handleNew = useCallback(() => {
    if (isDirty) { if (!window.confirm('You have unsaved changes. Start a new chart anyway?')) return; }
    newSong();
  }, [isDirty, newSong]);

  useEffect(() => {
    const isMac = navigator.platform.includes('Mac');
    const handle = (e: KeyboardEvent) => {
      const ctrl = isMac ? e.metaKey : e.ctrlKey;
      if (!ctrl) return;
      if (e.key === 's') { e.preventDefault(); handleSave(); }
      if (e.key === 'o') { e.preventDefault(); handleOpen(); }
      if (e.key === 'n') { e.preventDefault(); handleNew(); }
      if (e.key === 'p') { e.preventDefault(); onShowPrint(); }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [handleSave, handleOpen, handleNew, onShowPrint]);

  void setSongBarsPerRow; void setSongFeel; void setSongNote;
  void loadFromLocalStorage; void transposeBySemitones; void cursor;

  return (
    <header className="toolbar print:hidden border-b border-rule bg-paper flex-shrink-0"
      style={{ minHeight: 48, position: 'relative', zIndex: 100 }}>
      <style>{`header.toolbar::-webkit-scrollbar { display: none; }`}</style>
      <div className="flex items-center gap-1 px-2 sm:px-3"
        style={{ overflowX: 'auto', scrollbarWidth: 'none', minHeight: 48 }}>
        <input ref={fileInputRef} type="file" accept=".hchart,.json" className="hidden" onChange={handleFileChange} />

        {/* ── Group 1: Library + title ── */}
        <div className="toolbar-group flex items-center gap-2 flex-shrink-0">
          <button onClick={openLibrary} className="metadata-badge flex-shrink-0" title="My Charts">☰ Charts</button>
          {editingTitle ? (
            <input ref={titleRef} type="text" value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTitle();
                if (e.key === 'Escape') { setTitleValue(song.title); setEditingTitle(false); }
              }}
              className="song-title-input toolbar-title text-xl" spellCheck={false} />
          ) : (
            <button onClick={() => setEditingTitle(true)}
              className="toolbar-title font-display font-semibold text-xl text-ink-primary hover:text-ink-secondary transition-colors truncate cursor-text"
              title="Click to rename">
              {song.title}
            </button>
          )}
        </div>

        <div className="divider w-px h-5 bg-rule mx-1 flex-shrink-0" />

        {/* ── Group 2: Key + time sig + display mode ── */}
        <div className="toolbar-group flex items-center gap-1 flex-shrink-0">
          <KeySelector currentKey={song.defaultKey} onSelect={setSongKey} />
          <KeyDetectButton
            chords={extractChords(song.sections.flatMap((s) => s.bars))}
            currentKey={song.defaultKey}
            onSelectKey={setSongKey}
            label="detect"
          />
          <TimeSigSelector current={song.timeSignature} onChange={setSongTimeSignature} />
          <div className="divider w-px h-5 bg-rule mx-1 flex-shrink-0" />
          <div className="mode-toggle flex-shrink-0">
            {DISPLAY_MODES.map((m) => (
              <button key={m.value}
                className={`mode-toggle-btn ${song.displayMode === m.value ? 'active' : ''}`}
                onClick={() => setDisplayMode(m.value)}
                title={`Display as ${m.value}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="divider w-px h-5 bg-rule mx-1 flex-shrink-0" />

        {/* ── Group 3: Playback ── */}
        <div className="toolbar-group flex-shrink-0">
          {/* FIX: pass the four new volume props down to PlaybackControls */}
          <PlaybackControls
            playing={playing} isLoading={isLoading} cursor={cursor}
            tempo={song.tempo} onToggle={toggle} onStop={stop}
            showClick={showClick} onToggleClick={toggleClick}
            synthMode={synthMode} onSynthMode={handleSynthMode}
            onTempoChange={setSongTempo}
            chordVolume={chordVolume} onChordVolume={setChordVolume}
            clickVolume={clickVolume} onClickVolume={setClickVolume}
          />
        </div>

        <div className="divider w-px h-5 bg-rule mx-1 flex-shrink-0" />

        {/* ── Group 4: Annotation mode ── */}
        <div className="toolbar-group flex-shrink-0">
          <button
            onClick={onToggleAnnotationMode}
            title={annotationMode ? 'Exit annotation mode' : 'Add annotations'}
            className={['text-[11px] font-mono px-2 py-1 rounded border transition-colors',
              annotationMode
                ? 'bg-accent text-white border-accent'
                : 'border-rule text-ink-muted hover:bg-hover hover:text-ink-secondary'].join(' ')}>
            ✏️
          </button>
        </div>

        <div className="divider w-px h-5 bg-rule mx-1 flex-shrink-0" />

        {/* ── Group 5: Theme ── */}
        <div className="toolbar-group flex-shrink-0">
          <ThemeDropdown themeId={themeId} setThemeId={setThemeId} />
        </div>

        {/* ── Group 6: Right side ── */}
        <div className="toolbar-group flex items-center gap-1 flex-shrink-0 ml-1">
          <AnalysisPanel
            settings={song.analysisSettings ?? {
              enabled: false, showFunction: true,
              showBorrowed: true, showDegree: false, showSecondary: true,
            }}
            onChange={setAnalysisSettings}
          />

          <div className="divider w-px h-5 bg-rule mx-1 flex-shrink-0" />

          <PresenceAvatars />

          {user && <InvitesPanel onSongLoad={onSongLoad} />}
          {user && <button onClick={onShowShare} className="metadata-badge" title="Share chart">Share</button>}
          {user ? (
            <button onClick={signOut} className="metadata-badge" title={`Signed in as ${user.email}`}>Sign out</button>
          ) : (
            <button onClick={onShowAuth} className="metadata-badge border-accent text-accent hover:bg-accent/10" title="Sign in">Sign in</button>
          )}
          {user && (<><div className="divider w-px h-5 bg-rule flex-shrink-0" /><SyncIndicator /></>)}

          <div className="divider w-px h-5 bg-rule mx-1 flex-shrink-0" />

          <button className="control-btn" onClick={undo} disabled={!canUndo()} title="Undo (⌘Z)">↩</button>
          <button className="control-btn" onClick={redo} disabled={!canRedo()} title="Redo (⌘⇧Z)">↪</button>

          <div className="divider w-px h-5 bg-rule mx-1 flex-shrink-0" />

          <FileMenu
            isDirty={isDirty} onNew={handleNew} onSave={handleSave}
            onOpen={handleOpen} onExportJSON={handleExportJSON} onPrint={onShowPrint}
          />

          <div className="divider w-px h-5 bg-rule mx-1 flex-shrink-0" />

          <button
            onClick={onToggleDocs}
            title={docsOpen ? 'Close documentation' : 'Open documentation'}
            className={['text-[13px] font-mono w-7 h-7 flex items-center justify-center rounded border transition-colors flex-shrink-0',
              docsOpen ? 'bg-accent text-white border-accent' : 'border-rule text-ink-muted hover:bg-hover hover:text-ink-secondary'].join(' ')}
            aria-label="Toggle documentation">
            ?
          </button>
        </div>
      </div>
    </header>
  );
}